<template>
  <Dialog
    v-if="!hasNoPermission"
    v-model="isOpen"
    v-bind="$attrs"
    :content-class="'order-notification--dialog'"
    :persistent="dataLoading || saveInProgress"
    @save="onSave"
  >
    <template
      v-if="dataLoading"
      #content
    >
      <div class="text-center">
        <v-progress-circular
          indeterminate
          color="primary"
        />
      </div>
    </template>
    <template
      v-else
      #content
    >
      <div class="order-notification-wrapper">
        <div v-if="!notifications || notifications.length === 0">
          {{ $t("Notifications not found") }}
        </div>
        <v-radio-group
          v-else
          v-model="selectedNotificationState"
          :disabled="saveInProgress || !hasWritePermission"
          hide-details
        >
          <v-radio
            class="notifications--inactive"
            :label="$t('Don\'t receive any notifications for this order')"
            :value="null"
          />
          <v-radio
            class="notifications--active"
            :label="$t('Receive notifications for the following status changes')"
            :value="true"
          />
        </v-radio-group>
        <div class="notifications">
          <v-checkbox
            v-for="n in notifications"
            :key="n.notificationId"
            :model-value="isNotificationChecked(n)"
            :label="$t(n.name)"
            hide-details
            :disabled="isNotificationReadOnly(n)"
            @update:model-value="checked => onNotificationToggle(n, checked)"
          />
  <!--        :value="orderNotifications[n.notificationId]"-->

        </div>
      </div>
    </template>

    <template
      v-if="
        dataLoading ||
          !notifications ||
          notifications.length === 0 ||
          !hasWritePermission
      "
      #actions
    >
      &nbsp;
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch, useAttrs } from "vue";
import Dialog from "@/components/dialogs/Dialog.vue";
import {Notification, UpdateNotificationPayload} from "@/entities/notification";
import { PERMISSION } from "@/utils/constants";
import { useNotificationSelectionRules } from '@/composables/useNotificationSelectionRules';

const props = defineProps({
  statuses: { type: Array, default: () => [] },
  dataLoading: { type: Boolean, required: true, default: false },
  orderNotifications: { type: Array, required: true, default: () => [] },
  notifications: { type: Array, required: true, default: () => [] },
  permission: {
    type: String,
    default: () => PERMISSION.frontendCheck.none,
  },
  modelValue: { type: Boolean, required: true },
});

const {
  isForcedForExternalUser,
  isNotificationChecked: isNotificationCheckedByRules,
  isNotificationReadOnly: isNotificationReadOnlyByRules,
} = useNotificationSelectionRules();

const emit = defineEmits<{
  (e: "update:modelValue", value: boolean): void
  (e: "save", payload: UpdateNotificationPayload): void
}>()

const isOpen = computed({
  get: () => props.modelValue,
  set: (val) => emit("update:modelValue", val),
});

const attrs = useAttrs();

const selectedNotificationState = ref<boolean | null>(true);
const selectedNotifications = ref<string[]>([]);

const hasNoPermission = computed(
  () => props.permission === PERMISSION.frontendCheck.none
);
const hasWritePermission = computed(
  () => props.permission === PERMISSION.frontendCheck.readWrite
);

const saveInProgress = computed(
  () => (attrs && !!attrs["save-in-progress"]) ?? false
);

function syncWithProps() {
  selectedNotifications.value = (props.orderNotifications ?? []).map((n) =>
    String(n.notificationId)
  );
  const hasNonMandatory = (props.orderNotifications ?? []).some(
    (n) => !n.isMandatory
  );
  selectedNotificationState.value = hasNonMandatory ? true : null;
}

function isNotificationChecked(notification: Notification): boolean {
  return isNotificationCheckedByRules(
    notification,
    selectedNotificationState.value,
    selectedNotifications.value,
  );
}

function isNotificationReadOnly(notification: Notification): boolean {
  return isNotificationReadOnlyByRules(
    notification,
    selectedNotificationState.value,
    saveInProgress.value || !hasWritePermission.value
  );
}

function onNotificationToggle(notification: Notification, checked: boolean) {
  if (isForcedForExternalUser(notification)) {
    return;
  }

  const id = String(notification.notificationId);

  if (checked && !selectedNotifications.value.includes(id)) {
    selectedNotifications.value.push(id);
  }

  if (!checked) {
    selectedNotifications.value = selectedNotifications.value.filter(notificationId => notificationId !== id);
  }
}

function onSave() {
  const remove: Notification[] = [];
  const add: Notification[] = [];

  props.notifications.forEach((n) => {
    if (isForcedForExternalUser(n)) {
      return;
    }

    const inCurrent = props.orderNotifications.some(
      (on) => on.notificationId === n.notificationId
    );
    const inNew =
      selectedNotificationState.value &&
      selectedNotifications.value.includes(String(n.notificationId));

    if (inCurrent && !inNew) {
      remove.push(n);
    } else if (!inCurrent && inNew) {
      add.push(n);
    }
  });
  const payload: UpdateNotificationPayload = { remove, add }
  emit("save", payload);
}

watch(() => props.orderNotifications, syncWithProps, { immediate: true, deep: true });
watch(selectedNotificationState, (val) => {
  if (val === null) {
    if (!props.notifications?.length) return;
    selectedNotifications.value = selectedNotifications.value.filter((id) => {
      const desc = props.notifications.find(
        (n) => String(n.notificationId) === String(id)
      );
      return desc?.isMandatory === true;
    });
  }
});
</script>

<style lang="scss">
  .order-notification--dialog {
    .order-notification-wrapper {
      display: flex;
      flex-direction: column;
    }

    .v-card__text > .v-input--selection-controls {
      margin-top: 0;
      padding-top: 0;
    }

    .v-input--radio-group--column .v-radio.notifications--active {
      margin-bottom: 0;
    }

    .notifications--active {
      order: 1;
    }

    .notifications {
      order: 2;
      padding-left: 1.875rem;
    }

    .notifications--inactive {
      order: 3;
      margin-top: 2.5rem;
      margin-bottom: 2rem;
    }

    .v-radio-group,
    .v-radio-group > .v-input__control,
    .v-radio-group > .v-input__control > .v-selection-control-group {
      display: contents !important;
    }
  }
</style>
